import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getArtworkActivityInfo } from './artworkVisibilitySync';

const BOOK_TAGS = [
  'Around the world with 100 artists',
  "What's your story",
  'Complete the story',
  'Impressionism',
  'Pointillism',
  'Simple acts of art',
];

function norm(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

/** e.g. "Daily Activity 33", "Activity 33", "Art of the Day 33" */
export function parseDailyActivityNumberFromTitle(title) {
  if (!title || typeof title !== 'string') return '';
  const m =
    title.match(/(?:daily\s+)?(?:art\s+)?activity\s*#?\s*(\d+)/i) ||
    title.match(/art\s+of\s+the\s+day\s*#?\s*(\d+)/i);
  return m ? m[1] : '';
}

/**
 * Museum shares often have title "Daily Activity 33" but wrong activityId "1" in Firestore.
 * Prefer explicit title number over stored activityId when they disagree.
 */
export function resolveDailyActivityIdFromShare(share = {}) {
  const title = share.title || share.activityTitle || '';
  const fromTitle = parseDailyActivityNumberFromTitle(title);
  const fromField = share.activityId || share.activityNumber || share.nav?.activityId;
  const titleHasNumber = /(?:daily\s+)?(?:art\s+)?activity\s*#?\s*\d+/i.test(title);

  if (fromTitle && fromField && String(fromTitle) !== String(fromField) && titleHasNumber) {
    return String(fromTitle);
  }
  return String(fromTitle || fromField || '').trim();
}

export function normalizeBookFields(data = {}) {
  const book = data.book || data.tag || data.bookTag || '';
  const activityCode =
    data.activityCode || data.country || data.selectedCountry || data.activityNumber || '';
  return { book: String(book).trim(), activityCode: String(activityCode).trim() };
}

export function bookParticipationKey(book, activityCode) {
  const b = norm(book);
  const c = norm(activityCode);
  if (!b || !c) return '';
  return `${b}::${c}`;
}

/** Parse legacy titles like "Artwork from Around the world with 100 artists - Indonesia". */
export function parseBookShareFromTitle(title) {
  if (!title || typeof title !== 'string') return { book: '', activityCode: '' };
  const match = title.match(/^Artwork from (.+?)\s*-\s*(.+)$/i);
  if (match) {
    return { book: match[1].trim(), activityCode: match[2].trim() };
  }
  return { book: '', activityCode: '' };
}

export function enrichMuseumShare(share) {
  if (!share) return null;
  const base = { ...share };

  if (share.source === 'book') {
    let { book, activityCode } = normalizeBookFields(share);
    if (!book || !activityCode) {
      const parsed = parseBookShareFromTitle(share.title);
      book = book || parsed.book;
      activityCode = activityCode || parsed.activityCode;
    }
    if (!book && !activityCode) return null;
    const displayTitle =
      share.title ||
      (book && activityCode ? `${book} · ${activityCode}` : book || 'Book Gallery share');
    return {
      ...base,
      book,
      activityCode,
      tag: share.tag || book,
      title: displayTitle,
      sourceLabel: share.sourceLabel || 'Book Gallery',
      key: share.key || `book:${book}:${activityCode}`,
      nav: {
        tab: 'books',
        book,
        activityCode,
        submissionId: share.nav?.submissionId || share.id,
      },
    };
  }

  if (share.source === 'art-history') {
    const flow = share.flowActivityMeta || {};
    const meta = share.artworkMeta || {};
    const flowTypeKey = share.flowTypeKey || flow.artworkTypeKey || meta.artworkTypeKey;
    const flowLevel = share.flowLevel ?? flow.level ?? meta.level ?? '';
    const artworkId = share.artworkId || meta.id;
    return {
      ...base,
      flowTypeKey,
      flowLevel: String(flowLevel ?? ''),
      artworkId,
      nav: {
        tab: 'art-history',
        artworkId: artworkId || undefined,
        submissionId: share.nav?.submissionId || share.id,
      },
    };
  }

  if (share.source === 'activity') {
    const activityId = resolveDailyActivityIdFromShare(share);
    if (!activityId) return null;
    return {
      ...base,
      activityId,
      activityDocId: share.activityDocId || '',
      nav: {
        tab: 'activities',
        activityId,
        submissionId: share.nav?.submissionId || share.id,
      },
    };
  }

  return base;
}

/** Ways of Seeing shares are always visible on public profiles (no participation lock). */
export function requiresMuseumParticipation(share) {
  const enriched = enrichMuseumShare(share);
  if (!enriched) return false;
  return enriched.source !== 'ways-of-seeing';
}

export function getMuseumAttemptAction(share) {
  const enriched = enrichMuseumShare(share);
  if (!enriched) {
    return { label: 'Explore the Artist Salon', to: '/gallery/studio' };
  }

  switch (enriched.source) {
    case 'book': {
      const country = enriched.activityCode || 'your country';
      const isAroundWorld = norm(enriched.book).includes('around the world');
      return {
        label: isAroundWorld
          ? `Attempt ${country} in ARTmosphere Around the World – Part 1`
          : `Attempt this book activity (${country})`,
        to: '/artist-wall',
        state: {
          openBookMuseumUpload: true,
          bookTag: enriched.book || 'Around the world with 100 artists',
          selectedCountry: enriched.activityCode || '',
          bookId: 'book1',
        },
        hint: isAroundWorld
          ? `Upload your artwork for ${country} to the Artist Salon when you are done.`
          : 'Complete the book activity and share publicly to the Artist Salon.',
      };
    }
    case 'activity': {
      const aid = resolveDailyActivityIdFromShare(enriched);
      return {
        label: aid ? 'Go to this daily activity' : 'Go to Art of the Day',
        to: (() => {
          if (!aid) return '/daily-art-activity';
          const params = new URLSearchParams({ activityId: aid });
          const subId = enriched.nav?.submissionId || enriched.id;
          if (subId) params.set('submissionId', String(subId));
          return `/daily-art-activity?${params.toString()}`;
        })(),
        hint: 'Create and share your artwork publicly to unlock this gallery.',
      };
    }
    case 'art-history': {
      if (enriched.artworkId) {
        const artworkPayload = {
          id: enriched.artworkId,
          title: enriched.title,
          imageUrl: enriched.coverUrl,
        };
        return {
          label: 'Start this Art History artwork flow',
          to: '/learn-art-history/flow',
          state: {
            primaryArtwork: artworkPayload,
            artwork: artworkPayload,
            artworkTypeKey: enriched.flowTypeKey || undefined,
            level: enriched.flowLevel ? Number(enriched.flowLevel) || 1 : 1,
            embeddedInFlow: true,
            openModal: false,
            fromMuseumLock: true,
          },
          hint: 'Complete the artwork flow and share publicly to unlock this gallery.',
        };
      }
      if (enriched.flowTypeKey) {
        return {
          label: 'Start this Art History artwork flow',
          to: '/learn-art-history',
          state: { openModalForType: enriched.flowTypeKey },
          hint: 'Complete the flow and share your artwork to unlock what others created.',
        };
      }
      return {
        label: 'Explore Art History',
        to: '/learn-art-history',
        hint: 'Complete an activity and share publicly to unlock this gallery.',
      };
    }
    default:
      return { label: 'Explore the Artist Salon', to: '/gallery/studio' };
  }
}

export function deriveMuseumShareFromArtwork(art) {
  if (!art) return null;
  const info = getArtworkActivityInfo(art);
  if (!info) return null;

  if (info.activityType === 'book-co-learning') {
    let { book, activityCode } = normalizeBookFields(art);
    if (!book || !activityCode) {
      const parsed = parseBookShareFromTitle(art.title);
      book = book || parsed.book || art.tag || '';
      activityCode = activityCode || parsed.activityCode || '';
    }
    if (!book && !activityCode) return null;
    return enrichMuseumShare({
      source: 'book',
      book,
      activityCode,
      tag: art.tag,
      title: art.title,
      coverUrl: art.thumbnailUrl || art.imageUrl,
      id: art.bookArtworkId || art.id,
    });
  }

  if (info.activityType === 'ways-of-seeing') {
    const styleId =
      art.styleId || (art.styleName || '').toLowerCase().replace(/\s+/g, '') || null;
    if (!styleId) return null;
    return {
      source: 'ways-of-seeing',
      styleId,
      styleName: art.styleName || '',
      title: art.title || art.styleName || 'Ways of Seeing',
      id: art.id,
      nav: { tab: 'ways', styleId, submissionId: art.id },
    };
  }

  if (info.activityType === 'art-history') {
    const meta = art.artworkMeta || art.metadata?.artworkMeta || {};
    const flow = art.flowActivityMeta || meta.flowActivityMeta || {};
    const artworkId = art.artworkId || meta.id;
    const typeKey = flow.artworkTypeKey || meta.artworkTypeKey;
    const level = flow.level ?? meta.level;
    if (artworkId) {
      return {
        source: 'art-history',
        artworkId,
        title: art.title || meta.title || 'Art History artwork',
        id: art.id,
        nav: { tab: 'art-history', artworkId, submissionId: art.id },
      };
    }
    if (typeKey) {
      return {
        source: 'art-history',
        flowTypeKey: typeKey,
        flowLevel: String(level ?? ''),
        title: art.title || flow.title || meta.title || 'Art History activity',
        id: art.id,
        nav: { tab: 'art-history', submissionId: art.id },
      };
    }
  }

  if (
    info?.activityType === 'daily-activity' ||
    art.source === 'daily-activity' ||
    art.type === 'daily_activity' ||
    (art.activityId && info?.activityType !== 'book-co-learning')
  ) {
    const activityId = resolveDailyActivityIdFromShare({
      activityId: art.activityId || art.metadata?.activityId || art.metadata?.activityNumber,
      title: art.title || art.activityTitle,
      activityTitle: art.activityTitle,
    });
    if (!activityId) return null;
    return {
      source: 'activity',
      activityId,
      title: art.title || `Daily Activity ${activityId}`,
      id: art.id,
      nav: { tab: 'activities', activityId, submissionId: art.id },
    };
  }

  return null;
}

function bookFieldsMatch(data, book, activityCode) {
  const fields = normalizeBookFields(data);
  const parsed = !fields.book || !fields.activityCode ? parseBookShareFromTitle(data.title) : {};
  const dataBook = fields.book || parsed.book;
  const dataCode = fields.activityCode || parsed.activityCode;

  const bookNorm = norm(book);
  const codeNorm = norm(activityCode);
  if (!bookNorm || !codeNorm) return false;

  return norm(dataBook) === bookNorm && norm(dataCode) === codeNorm;
}

/** Load all co-learning participation keys for the signed-in viewer (fast set lookup). */
export async function loadViewerParticipationKeys(viewerUid) {
  const keys = new Set();
  if (!viewerUid) return keys;

  try {
    const [bookPartsSnap, bookArtSnap, profileArtSnap, submissionsSnap, waysSnap, ahSnap] =
      await Promise.all([
        getDocs(query(collection(db, 'bookActivityParticipants'), where('uid', '==', viewerUid))),
        getDocs(query(collection(db, 'bookArtworks'), where('uid', '==', viewerUid))),
        getDocs(query(collection(db, 'artworks'), where('uid', '==', viewerUid))),
        getDocs(query(collection(db, 'submissions'), where('uid', '==', viewerUid))),
        getDocs(query(collection(db, 'tutorialArtworks'), where('userId', '==', viewerUid))),
        getDocs(query(collection(db, 'artHistorySharedArtworks'), where('uid', '==', viewerUid))),
      ]);

    bookPartsSnap.docs.forEach((d) => {
      const { book, activityCode } = normalizeBookFields(d.data());
      const key = bookParticipationKey(book, activityCode);
      if (key) keys.add(`book:${key}`);
    });

    bookArtSnap.docs.forEach((d) => {
      const data = d.data();
      const { book, activityCode } = normalizeBookFields(data);
      const parsed = parseBookShareFromTitle(data.title);
      const key = bookParticipationKey(
        book || parsed.book,
        activityCode || parsed.activityCode
      );
      if (key) keys.add(`book:${key}`);
    });

    profileArtSnap.docs.forEach((d) => {
      const data = d.data();
      if (!data.isBookArtwork && !data.bookArtworkId && !isBookCoLearningArtwork(data)) return;
      const { book, activityCode } = normalizeBookFields(data);
      const parsed = parseBookShareFromTitle(data.title);
      const key = bookParticipationKey(
        book || parsed.book || data.tag,
        activityCode || parsed.activityCode
      );
      if (key) keys.add(`book:${key}`);
    });

    submissionsSnap.docs.forEach((d) => {
      const aid = d.data().activityId;
      if (aid) keys.add(`activity:${norm(aid)}`);
    });

    waysSnap.docs.forEach((d) => {
      const sid =
        d.data().styleId || (d.data().styleName || '').toLowerCase().replace(/\s+/g, '');
      if (sid) keys.add(`ways:${sid}`);
    });

    ahSnap.docs.forEach((d) => {
      const data = d.data();
      if (data.artworkId) keys.add(`ah:artwork:${norm(data.artworkId)}`);
      const flow = data.flowActivityMeta || {};
      const meta = data.artworkMeta || {};
      const typeKey = flow.artworkTypeKey || meta.artworkTypeKey;
      if (typeKey) {
        keys.add(`ah:flow:${norm(typeKey)}:${norm(String(flow.level ?? meta.level ?? ''))}`);
      }
    });
  } catch (err) {
    console.warn('[museumShareAccess] loadViewerParticipationKeys failed:', err);
  }

  return keys;
}

export function shareMatchesParticipationKeys(share, keys) {
  const enriched = enrichMuseumShare(share);
  if (!enriched || !keys?.size) return false;

  switch (enriched.source) {
    case 'book': {
      const key = bookParticipationKey(enriched.book, enriched.activityCode);
      return key ? keys.has(`book:${key}`) : false;
    }
    case 'activity':
      return enriched.activityId ? keys.has(`activity:${norm(enriched.activityId)}`) : false;
    case 'ways-of-seeing': {
      const sid =
        enriched.styleId ||
        (enriched.styleName || '').toLowerCase().replace(/\s+/g, '');
      return sid ? keys.has(`ways:${sid}`) : false;
    }
    case 'art-history': {
      if (enriched.artworkId) return keys.has(`ah:artwork:${norm(enriched.artworkId)}`);
      if (enriched.flowTypeKey) {
        return keys.has(
          `ah:flow:${norm(enriched.flowTypeKey)}:${norm(String(enriched.flowLevel ?? ''))}`
        );
      }
      return false;
    }
    default:
      return false;
  }
}

/** Whether the signed-in viewer has contributed to the same museum activity as this share. */
export async function viewerParticipatedInShare(share, viewerUid) {
  if (!share || !viewerUid) return false;

  const enriched = enrichMuseumShare(share);
  if (!enriched) return false;

  try {
    switch (enriched.source) {
      case 'book': {
        const { book, activityCode } = normalizeBookFields(enriched);
        if (!book || !activityCode) return false;

        const [partsSnap, artSnap, profileSnap] = await Promise.all([
          getDocs(query(collection(db, 'bookActivityParticipants'), where('uid', '==', viewerUid))),
          getDocs(query(collection(db, 'bookArtworks'), where('uid', '==', viewerUid))),
          getDocs(query(collection(db, 'artworks'), where('uid', '==', viewerUid))),
        ]);

        if (partsSnap.docs.some((d) => bookFieldsMatch(d.data(), book, activityCode))) return true;
        if (artSnap.docs.some((d) => bookFieldsMatch(d.data(), book, activityCode))) return true;
        if (
          profileSnap.docs.some((d) => {
            const data = d.data();
            return (
              (data.isBookArtwork || data.bookArtworkId || isBookCoLearningArtwork(data)) &&
              bookFieldsMatch(data, book, activityCode)
            );
          })
        ) {
          return true;
        }

        return false;
      }
      case 'activity': {
        if (!enriched.activityId) return false;
        const snap = await getDocs(
          query(collection(db, 'submissions'), where('uid', '==', viewerUid))
        );
        return snap.docs.some((d) => norm(d.data().activityId) === norm(enriched.activityId));
      }
      case 'ways-of-seeing': {
        const styleId =
          enriched.styleId ||
          (enriched.styleName || '').toLowerCase().replace(/\s+/g, '') ||
          null;
        if (!styleId) return false;
        const snap = await getDocs(
          query(collection(db, 'tutorialArtworks'), where('userId', '==', viewerUid))
        );
        return snap.docs.some((d) => {
          const sid =
            d.data().styleId || (d.data().styleName || '').toLowerCase().replace(/\s+/g, '');
          return sid === styleId;
        });
      }
      case 'art-history': {
        const snap = await getDocs(
          query(collection(db, 'artHistorySharedArtworks'), where('uid', '==', viewerUid))
        );
        if (enriched.artworkId) {
          return snap.docs.some((d) => norm(d.data().artworkId) === norm(enriched.artworkId));
        }
        if (enriched.flowTypeKey) {
          return snap.docs.some((d) => {
            const data = d.data();
            const flow = data.flowActivityMeta || {};
            const meta = data.artworkMeta || {};
            const typeKey = flow.artworkTypeKey || meta.artworkTypeKey;
            const level = String(flow.level ?? meta.level ?? '');
            return (
              norm(typeKey) === norm(enriched.flowTypeKey) &&
              level === String(enriched.flowLevel ?? '')
            );
          });
        }
        return false;
      }
      default:
        return false;
    }
  } catch (err) {
    console.warn('[museumShareAccess] participation check failed:', err);
    return false;
  }
}

export function buildGalleryStudioUrl(share) {
  const enriched = enrichMuseumShare(share);
  if (!enriched?.nav) return '/gallery/studio';
  const params = new URLSearchParams();
  params.set('tab', enriched.nav.tab || 'activities');
  if (enriched.nav.book) params.set('book', enriched.nav.book);
  if (enriched.nav.activityCode) params.set('activityCode', enriched.nav.activityCode);
  if (enriched.nav.activityId) params.set('activityId', enriched.nav.activityId);
  if (enriched.nav.styleId) params.set('styleId', enriched.nav.styleId);
  if (enriched.nav.artworkId) params.set('artworkId', enriched.nav.artworkId);
  if (enriched.nav.submissionId) params.set('submissionId', enriched.nav.submissionId);
  return `/gallery/studio?${params.toString()}`;
}

export function getLockedShareMessage(share) {
  const enriched = enrichMuseumShare(share);
  if (!enriched) {
    return 'Join this Artist Salon activity and share your artwork publicly to unlock and explore what other artists created.';
  }

  const attempt = getMuseumAttemptAction(enriched);

  switch (enriched.source) {
    case 'book': {
      const country = enriched.activityCode || 'your country';
      const isAroundWorld = norm(enriched.book).includes('around the world');
      if (isAroundWorld) {
        return `To see what other artists created, attempt ${country} from ARTmosphere Around the World – Part 1, then upload and share your artwork to the Artist Salon.`;
      }
      return `Complete the book activity for ${country}, share your artwork to the Artist Salon, then return to explore this gallery.`;
    }
    case 'activity':
      return 'Join this daily activity, create your artwork, and share it publicly to unlock and see what other artists created.';
    case 'art-history':
      return 'Start the Art History artwork flow for this piece, share your creation publicly, then come back to explore the co-learning gallery.';
    default:
      return attempt.hint || 'Contribute to this Artist Salon activity and share your artwork publicly to unlock and explore what other artists created.';
  }
}

export function isBookCoLearningArtwork(art) {
  if (!art) return false;
  if (getArtworkActivityInfo(art)?.activityType === 'book-co-learning') return true;
  const tag = String(art.tag || '');
  return BOOK_TAGS.some((t) => tag.includes(t) || norm(tag) === norm(t));
}
